import React, { useState } from 'react';
import { useShift } from '../../context/ShiftContext';
import { useUser } from '../../context/UserContext';
import { format, parseISO } from 'date-fns';
import { CheckIcon, XIcon, ClockIcon } from 'lucide-react';
const ApproveRequests = () => {
  const {
    requests,
    shifts,
    holidays,
    updateRequest,
    addActivityLog
  } = useShift();
  const {
    users,
    user: currentUser
  } = useUser();
  const [successMessage, setSuccessMessage] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  // Filter requests based on selected filter
  const filteredRequests = requests.filter(r => {
    if (filter === 'all') return true;
    return r.status === filter;
  });
  const handleApprove = (requestId: number) => {
    const request = requests.find(r => r.id === requestId);
    const requestUser = users.find(u => u.id === request?.userId);
    const shiftInfo = request ? getShiftInfo(request.shiftId) : null;
    updateRequest(requestId, 'approved', currentUser?.id);
    addActivityLog({
      userId: currentUser?.id,
      userName: currentUser?.name,
      action: 'Approved Request',
      details: `Approved ${requestUser?.name}'s request for ${shiftInfo?.date ? format(parseISO(shiftInfo.date), 'MMM d, yyyy') : 'shift'}`,
      category: 'request'
    });
    setSuccessMessage('Request approved successfully!');
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  const handleReject = (requestId: number) => {
    const request = requests.find(r => r.id === requestId);
    const requestUser = users.find(u => u.id === request?.userId);
    const shiftInfo = request ? getShiftInfo(request.shiftId) : null;
    updateRequest(requestId, 'rejected', currentUser?.id);
    addActivityLog({
      userId: currentUser?.id,
      userName: currentUser?.name,
      action: 'Rejected Request',
      details: `Rejected ${requestUser?.name}'s request for ${shiftInfo?.date ? format(parseISO(shiftInfo.date), 'MMM d, yyyy') : 'shift'}`,
      category: 'request'
    });
    setSuccessMessage('Request rejected successfully!');
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };
  const getShiftInfo = (shiftId: number) => {
    const shift = shifts.find(s => s.id === shiftId);
    if (!shift) return {
      date: '',
      type: ''
    };
    const holiday = shift.type === 'holiday' ? holidays.find(h => h.date === shift.date)?.name : null;
    return {
      date: shift.date,
      type: shift.type,
      holiday
    };
  };
  const getStatusBadge = (status: string) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status as keyof typeof colors]}`} data-id="element-93">
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>;
  };
  return <div data-id="element-94">
      <div className="flex justify-between items-center mb-6" data-id="element-95">
        <h3 className="text-xl font-semibold text-gray-700" data-id="element-96">
          Approve Requests
        </h3>
        <div className="flex space-x-2" data-id="element-97">
          <button onClick={() => setFilter('pending')} className={`px-3 py-1 text-sm rounded-md ${filter === 'pending' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-98">
            Pending
          </button>
          <button onClick={() => setFilter('approved')} className={`px-3 py-1 text-sm rounded-md ${filter === 'approved' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-99">
            Approved
          </button>
          <button onClick={() => setFilter('rejected')} className={`px-3 py-1 text-sm rounded-md ${filter === 'rejected' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-100">
            Rejected
          </button>
          <button onClick={() => setFilter('all')} className={`px-3 py-1 text-sm rounded-md ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`} data-id="element-101">
            All
          </button>
        </div>
      </div>
      {successMessage && <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4" data-id="element-102">
          {successMessage}
        </div>}
      {filteredRequests.length > 0 ? <div className="bg-white shadow overflow-hidden sm:rounded-md" data-id="element-103">
          <ul className="divide-y divide-gray-200" data-id="element-104">
            {filteredRequests.map(request => {
          const requestUser = users.find(u => u.id === request.userId);
          const approver = request.approvedBy ? users.find(u => u.id === request.approvedBy) : null;
          const shiftInfo = getShiftInfo(request.shiftId);
          return <li key={request.id} data-id="element-105">
                  <div className="px-4 py-4 sm:px-6" data-id="element-106">
                    <div className="flex items-center justify-between" data-id="element-107">
                      <div className="flex-1" data-id="element-108">
                        <div className="flex items-center justify-between mb-2" data-id="element-109">
                          <h4 className="text-lg font-medium text-gray-900" data-id="element-110">
                            {requestUser?.name || 'Unknown User'} requested a
                            shift
                          </h4>
                          {getStatusBadge(request.status)}
                        </div>
                        <p className="text-sm text-gray-500" data-id="element-111">
                          {format(parseISO(shiftInfo.date), 'MMMM d, yyyy')} (
                          {format(parseISO(shiftInfo.date), 'EEEE')})
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-112">
                          Shift Type:{' '}
                          {shiftInfo.type.charAt(0).toUpperCase() + shiftInfo.type.slice(1)}
                          {shiftInfo.holiday && ` - ${shiftInfo.holiday}`}
                        </p>
                        <div className="flex items-center text-xs text-gray-400 mt-2" data-id="element-113">
                          <ClockIcon size={12} className="mr-1" data-id="element-114" />
                          Requested:{' '}
                          {format(parseISO(request.createdAt), 'MMM d, yyyy h:mm a')}
                        </div>
                        {request.approvedAt && approver && <p className="text-xs text-gray-500 mt-1" data-id="element-115">
                            {request.status === 'approved' ? 'Approved' : 'Rejected'}{' '}
                            by {approver.name} on{' '}
                            {format(parseISO(request.approvedAt), 'MMM d, yyyy h:mm a')}
                          </p>}
                      </div>
                      {request.status === 'pending' && <div className="flex space-x-3 ml-4" data-id="element-116">
                          <button onClick={() => handleReject(request.id)} className="inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500" data-id="element-117">
                            <XIcon size={16} className="mr-2" data-id="element-118" />
                            Reject
                          </button>
                          <button onClick={() => handleApprove(request.id)} className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500" data-id="element-119">
                            <CheckIcon size={16} className="mr-2" data-id="element-120" />
                            Approve
                          </button>
                        </div>}
                    </div>
                  </div>
                </li>;
        })}
          </ul>
        </div> : <div className="text-center py-10" data-id="element-121">
          <p className="text-gray-500" data-id="element-122">
            {filter === 'pending' ? 'There are no pending requests to approve.' : `No ${filter} requests found.`}
          </p>
        </div>}
    </div>;
};
export default ApproveRequests;